import { EventType, LogLevel, PublicClientApplication } from '@azure/msal-browser'

import { Paths } from './common/utils'

// Browser check variables
// If you support IE, our recommendation is that you sign-in using Redirect APIs
// If you as a developer are testing using Edge InPrivate mode, please add "isEdge" to the if check
const ua = window.navigator.userAgent
const msie = ua.indexOf('MSIE ')
const msie11 = ua.indexOf('Trident/')
const msedge = ua.indexOf('Edge/')
const firefox = ua.indexOf('Firefox')
const isIE = msie > 0 || msie11 > 0
const isEdge = msedge > 0
const isFirefox = firefox > 0 // Only needed if you need to support the redirect flow in Firefox incognito

const tenantSubdomain = import.meta.env.VITE_AUTH_TENANT_SUBDOMAIN
const signInPolicy = import.meta.env.VITE_AUTH_SIGN_IN_POLICY
const signUpPolicy = import.meta.env.VITE_AUTH_SIGN_UP_POLICY
const xeroPolicy = import.meta.env.VITE_AUTH_XERO_POLICY
const resetPasswordPolicy = import.meta.env.VITE_AUTH_RESET_PASSWORD_POLICY
const editProfilePolicy = import.meta.env.VITE_AUTH_EDIT_PROFILE_POLICY
const clientId = import.meta.env.VITE_AUTH_CLIENT_ID
const cacheLocation = import.meta.env.VITE_AUTH_CACHE_LOCATION

// With the changes below, we are allowing redirects to whatever subdomain has loaded the webstore app as well as
// allowing loading the app from the main domain

const redirectUri = `https://${window.location.host}`
const registerRedirectUri = `${redirectUri}/register`
const postLogoutRedirectUri = `${redirectUri}/signout`

const tenant = `${tenantSubdomain}.onmicrosoft.com`
const b2cInstance = `https://${tenantSubdomain}.b2clogin.com/tfp/`
const scopes = [`https://${tenant}/api/quotemate.api.read`, `https://${tenant}/api/quotemate.api.write`]
const signInAuthority = `${b2cInstance}${tenant}/${signInPolicy}`
const signUpAuthority = `${b2cInstance}${tenant}/${signUpPolicy}`
const xeroAuthority = `${b2cInstance}${tenant}/${xeroPolicy}`
const resetPasswordAuthority = `${b2cInstance}${tenant}/${resetPasswordPolicy}`
const editProfileAuthority = `${b2cInstance}${tenant}/${editProfilePolicy}`
const logLevel = import.meta.env.VITE_ENV === 'development' ? LogLevel.Warning : LogLevel.Error

const params = new URLSearchParams(window.location.search)
const hasXero = params.has('xero')
const authority = hasXero ? xeroAuthority : signInAuthority

export const msalConfig = {
    auth: {
        clientId: clientId,
        authority: authority,
        knownAuthorities: [signInAuthority, signUpAuthority, xeroAuthority],
        validateAuthority: false,
        redirectUri: redirectUri,
        postLogoutRedirectUri: postLogoutRedirectUri,
        scopes: scopes,
    },
    cache: {
        cacheLocation,
        storeAuthStateInCookie: isIE || isEdge || isFirefox,
    },
    system: {
        loggerOptions: {
            logLevel: logLevel,
            loggerCallback: (level, message, containsPii) => {
                if (containsPii) {
                    return
                }
                switch (level) {
                    case LogLevel.Error:
                        console.error(message)
                        return
                    case LogLevel.Info:
                        console.info(message)
                        return
                    case LogLevel.Verbose:
                        console.debug(message)
                        return
                    case LogLevel.Warning:
                        console.warn(message)
                        return
                    default:
                        return
                }
            },
        },
    },
}

// Add here scopes for id token to be used at MS Identity Platform endpoints.
export const loginRequest = {
    scopes,
    authority: authority,
}

export const resetPasswordRequest = {
    scopes,
    authority: resetPasswordAuthority,
}

export const editProfileRequest = {
    scopes,
    authority: editProfileAuthority,
}

export const signUpRequest = {
    scopes,
    authority: signUpAuthority,
    redirectUri: registerRedirectUri,
}

export const tokenRequest = {
    scopes,
}

export const msalInstance = new PublicClientApplication(msalConfig)

// Initialize MSAL instance as required for v4+
let isInitialized = false
let initializationPromise = null

/**
 * Initialize MSAL instance.
 * @deprecated Use MsalService.initialize() for consistent initialization patterns.
 * This export is maintained only for MsalService internal use.
 * @internal
 */
export const initializeMsal = async () => {
    if (isInitialized) {
        // Already initialized, just return
        return
    }

    // If initialization is already in progress, wait for it
    if (initializationPromise) {
        console.info('[MSAL] Initialization already in progress, waiting...')
        return await initializationPromise
    }

    // Start initialization and store the promise
    initializationPromise = (async () => {
        try {
            // Initialize MSAL
            await msalInstance.initialize()
            isInitialized = true
            console.info('[MSAL] Initialization completed successfully')

            msalInstance.enableAccountStorageEvents()

            // Account selection logic is app dependent. Adjust as needed for different use cases.
            const accounts = msalInstance.getAllAccounts()
            if (accounts && accounts.length > 0) {
                msalInstance.setActiveAccount(accounts[0])
            }

            // Add event callback after initialization
            msalInstance.addEventCallback((message) => {
                try {
                    // Validate message object exists
                    if (!message || typeof message !== 'object') {
                        console.warn('[MSAL] Invalid event message received:', message)
                        return
                    }

                    const { error, eventType, payload } = message

                    // Log all events in development for debugging
                    if (import.meta.env.VITE_ENV === 'development') {
                        console.info('[MSAL] Event received:', {
                            eventType,
                            hasPayload: !!payload,
                            hasError: !!error,
                            timestamp: new Date().toISOString(),
                        })
                    }

                    // Handle interaction_in_progress errors gracefully
                    if (error && 'errorCode' in error && error.errorCode === 'interaction_in_progress') {
                        console.warn('[MSAL] Interaction already in progress, ignoring concurrent request')
                        return
                    }

                    switch (eventType) {
                        case EventType.LOGIN_SUCCESS:
                            if (payload && 'account' in payload && payload.account) {
                                const account = payload.account
                                console.info('[MSAL] Login successful, setting active account:', account.username)
                                msalInstance.setActiveAccount(account)
                            } else {
                                console.warn('[MSAL] LOGIN_SUCCESS event missing account payload')
                            }
                            break

                        case EventType.ACCOUNT_REMOVED:
                            console.info('[MSAL] Account removed, clearing active account and redirecting')
                            msalInstance.setActiveAccount(null)

                            // Use navigate instead of direct window.location for better error handling
                            try {
                                window.location.href = Paths.SIGNOUT_PATHNAME
                            } catch (navigationError) {
                                console.error('[MSAL] Failed to navigate to signout page:', navigationError)
                                // Fallback to reload if navigation fails
                                window.location.reload()
                            }
                            break

                        case EventType.ACTIVE_ACCOUNT_CHANGED:
                            // Log the event but don't redirect automatically to avoid unexpected redirects
                            // Users will navigate as appropriate based on their interactions
                            if (import.meta.env.VITE_ENV === 'development') {
                                console.info('[MSAL] Active account changed, no automatic redirect')
                            }
                            break

                        case EventType.LOGIN_FAILURE:
                            console.error('[MSAL] Login failed:', error?.message || 'Unknown error')
                            break

                        case EventType.ACQUIRE_TOKEN_FAILURE:
                            console.error('[MSAL] Token acquisition failed:', error?.message || 'Unknown error')
                            break

                        case EventType.SSO_SILENT_FAILURE:
                            console.warn('[MSAL] SSO silent failure:', error?.message || 'Silent authentication failed')
                            break

                        default:
                            // Log unhandled events in development for debugging
                            if (import.meta.env.VITE_ENV === 'development' && eventType) {
                                console.debug('[MSAL] Unhandled event type:', eventType)
                            }
                            break
                    }
                } catch (callbackError) {
                    // Ensure callback errors don't break authentication flow
                    console.error('[MSAL] Event callback error:', callbackError)
                }
            })
        } catch (error) {
            console.error('[MSAL] Initialization failed:', error)
            initializationPromise = null // Reset promise on failure to allow retry
            throw error
        }
    })()

    return await initializationPromise
}
